import { useEffect, useState } from 'react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import {
  ScheduleFFHelpers,
  useCalendarEventsConfigurationShallowStore,
  useCalendarEventsFormTypeShallowStore,
} from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { OnlineSchedulingAppointmentPromoCard } from '@frontend/self-upgrade';
import { Tray, useModalControl } from '@frontend/design-system';
import { CalendarEventSelector } from '../../components/CalendarEventsSelector';
import { CalendarEventsEnums } from '../../types';
import { isUUID } from '../../utils';
import { DEFAULT_CONFIGURED_CALENDAR_EVENT_VALUE } from '../constant';
import { CalendarEventsModalContent } from './CalendarEventsModalContent';

export const CalendarEventsContainer = () => {
  const { isEnabled: isNewApptPanelFFEnabled } = ScheduleFFHelpers.useNewApptPanelFeatureFlag();
  const { selectedLocationIds } = useAppScopeStore();
  const { configuredCalendarEvent, setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore(
    'configuredCalendarEvent',
    'setConfiguredCalendarEvent'
  );

  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM();

  const { selectedForm, setSelectedForm, setSelectedPerson } = useCalendarEventsFormTypeShallowStore(
    'selectedForm',
    'setSelectedForm',
    'setSelectedPerson'
  );

  const calendarEventSelectionModalProps = useModalControl();
  const eventPanelModalProps = useModalControl({ disableReturnFocus: true });
  const { modalProps, openModal, closeModal } = eventPanelModalProps;

  const [locationIdsForOnlineSchedulePromo, setLocationIdsForOnlineSchedulePromo] = useState<string[]>([]);

  // useEffect to manage the modal or popover triggers
  useEffect(() => {
    const hasProviderDetails = configuredCalendarEvent?.providerId || configuredCalendarEvent?.providerName;

    const isValidEvent = configuredCalendarEvent?.isValid;

    const calendarEventType = configuredCalendarEvent?.calendarEventType ?? '';

    if (calendarEventType === CalendarEventsEnums.APPOINTMENT_EVENT) {
      setSelectedForm(calendarEventType);
      openModal();
      return;
    }

    if (isValidEvent && !hasProviderDetails) {
      setSelectedForm(CalendarEventsEnums.OFFICE_HOURS_OUT_OF_OFFICE_EVENT);
      openModal();
    } else if (
      isValidEvent &&
      calendarEventType &&
      calendarEventType !== CalendarEventsEnums.OFFICE_HOURS_OUT_OF_OFFICE_EVENT
    ) {
      setSelectedForm(calendarEventType);
      openModal();
    } else if (configuredCalendarEvent && configuredCalendarEvent.isValid) {
      setSelectedForm('');
      calendarEventSelectionModalProps.openModal();
    }
  }, [configuredCalendarEvent]);

  useEffect(() => {
    if (selectedForm) {
      openModal();
    }
  }, [selectedForm]);

  useEffect(() => {
    if (selectedLocationIds) {
      handleEventsSelectionCancelButtonClick();
      closeModal();
    }
  }, [selectedLocationIds]);

  const onCalendarEventSelect = (event: CalendarEventsEnums) => {
    if (event) {
      setSelectedForm(event);
      openModal();
    }
  };

  const handleModalClose = () => {
    setSelectedForm('');
    setSelectedPerson(null);
    closeModal();
    setConfiguredCalendarEvent(DEFAULT_CONFIGURED_CALENDAR_EVENT_VALUE);
  };

  const handleEventsSelectionCancelButtonClick = () => {
    setSelectedForm('');
    calendarEventSelectionModalProps.closeModal();
    setConfiguredCalendarEvent(DEFAULT_CONFIGURED_CALENDAR_EVENT_VALUE);
  };

  const handleAppointmentSuccess = (appointment?: Appointment) => {
    // Added 2s delay to avoid conflicts with the toast message
    setTimeout(() => {
      appointment?.clientLocationIds?.length && setLocationIdsForOnlineSchedulePromo(appointment.clientLocationIds);
    }, 2000);
  };

  const isValidProviderId = configuredCalendarEvent?.providerId && isUUID(configuredCalendarEvent.providerId);

  const shouldShowCalendarEventsPopover =
    hasWSM && ((!configuredCalendarEvent?.providerId && !configuredCalendarEvent?.locationId) || isValidProviderId);

  return (
    <>
      {shouldShowCalendarEventsPopover && (
        <CalendarEventSelector
          modalProps={calendarEventSelectionModalProps.modalProps}
          onCalendarEventSelect={onCalendarEventSelect}
          onModalClose={handleEventsSelectionCancelButtonClick}
        />
      )}

      <Tray
        width='medium'
        autoFocusTimeout={3600000 * 2}
        {...modalProps}
        onClose={handleModalClose}
        style={
          isNewApptPanelFFEnabled && selectedForm === CalendarEventsEnums.APPOINTMENT_EVENT
            ? { padding: 0, gap: 0 }
            : undefined
        }
      >
        <CalendarEventsModalContent closeModal={handleModalClose} onAppointmentSuccess={handleAppointmentSuccess} />
      </Tray>

      {!modalProps.show && (
        <OnlineSchedulingAppointmentPromoCard
          locationIds={locationIdsForOnlineSchedulePromo}
          onClose={() => setLocationIdsForOnlineSchedulePromo([])}
        />
      )}
    </>
  );
};
